import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import zrsLogo from "@/brand/zrsLogo";
import { parseNavHashTo, scrollToSectionId } from "@/lib/scrollToSection";
import { userDisplayName } from "@/lib/userDisplay";
import { useI18n } from "@/i18n/I18nProvider";
import LanguageSwitch from "./LanguageSwitch";

/** `id` mantém os data-testid estáveis (ex. nav-link-servidores) em qualquer idioma. */
const LINKS = [
    { id: "home", key: "nav.home", to: "/#home" },
    { id: "servidores", key: "nav.servers", to: "/#servidores" },
    { id: "vips", key: "nav.vips", to: "/#vips" },
    { id: "kits", key: "nav.kits", to: "/#kits" },
    { id: "loja", key: "nav.shop", to: "/#loja" },
    { id: "discord", key: "nav.discord", to: "/#discord" },
];

function NavHashLink({ to, className, children, onAfterClick, ...rest }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { path, hash } = parseNavHashTo(to);

    const handleClick = (e) => {
        onAfterClick?.();

        const onHome = location.pathname === path;

        if (!hash && onHome) {
            e.preventDefault();
            scrollToSectionId("");
            window.history.replaceState(null, "", path);
            return;
        }

        if (hash && onHome) {
            e.preventDefault();
            scrollToSectionId(hash);
            window.history.replaceState(null, "", `${path}#${hash}`);
            return;
        }

        if (hash && !onHome) {
            e.preventDefault();
            navigate(`${path}#${hash}`);
        }
    };

    return (
        <Link to={to} className={className} onClick={handleClick} {...rest}>
            {children}
        </Link>
    );
}

export default function Navbar({ onLoginClick, onSignOut, discordUrl, user }) {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [entered, setEntered] = useState(false);
    const location = useLocation();
    const onAccountPage = location.pathname === "/conta";
    const { t } = useI18n();

    useEffect(() => {
        setEntered(true);
        const onScroll = () => setScrolled(window.scrollY > 30);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <>
            <nav
                data-testid="navbar"
                className={`fixed top-4 md:top-5 left-1/2 -translate-x-1/2 z-50 transition-all duration-700 ${
                    entered
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 -translate-y-6"
                }`}
                style={{ width: "min(1180px, calc(100% - 24px))" }}
            >
                <div
                    className={`glass rounded-full flex items-center gap-2 pl-3 pr-3 py-2 md:pl-4 md:pr-2 md:py-2 ${
                        scrolled ? "shadow-[0_10px_40px_-12px_rgba(0,0,0,0.8)]" : ""
                    }`}
                >
                    <Link
                        to="/"
                        data-testid="navbar-logo"
                        className="flex shrink-0 items-center pl-1 pr-2"
                        aria-label={t("nav.logoAria")}
                    >
                        <img
                            src={zrsLogo}
                            alt=""
                            aria-hidden
                            className="h-8 w-auto max-w-[7.5rem] object-contain md:h-9"
                            style={{ filter: "drop-shadow(0 0 8px rgba(255,184,0,0.5))" }}
                        />
                    </Link>

                    <div className="hidden md:block h-6 w-px bg-white/10 mx-1" />

                    <ul className="hidden md:flex items-center gap-1 flex-1 justify-center">
                        {LINKS.map((l) => (
                            <li key={l.to}>
                                <NavHashLink
                                    to={l.to}
                                    data-testid={`nav-link-${l.id}`}
                                    className="nav-link relative px-3 py-2 text-[13px] font-medium text-zrs-offwhite/75 hover:text-gold transition-colors rounded-full"
                                >
                                    {t(l.key)}
                                </NavHashLink>
                            </li>
                        ))}
                    </ul>

                    <div className="hidden md:flex items-center gap-2 shrink-0">
                        <LanguageSwitch />
                        {user ? (
                            <>
                                <Link
                                    to="/conta"
                                    data-testid="navbar-account-link"
                                    className={`rounded-full px-3 py-2 text-[12px] font-semibold uppercase tracking-[0.12em] transition-colors ${
                                        onAccountPage
                                            ? "border border-gold/40 bg-gold/10 text-gold"
                                            : "border border-white/10 text-zrs-offwhite/85 hover:border-gold/35 hover:bg-gold/5 hover:text-gold"
                                    }`}
                                >
                                    {t("nav.account")}
                                </Link>
                                <div
                                    data-testid="navbar-user"
                                    className="flex items-center gap-2 max-w-[160px] pl-1"
                                >
                                    {user.user_metadata?.avatar_url ? (
                                        <img
                                            src={user.user_metadata.avatar_url}
                                            alt=""
                                            className="w-8 h-8 rounded-full border border-white/10 object-cover shrink-0"
                                        />
                                    ) : null}
                                    <span className="text-[13px] font-medium text-zrs-offwhite/90 truncate">
                                        {userDisplayName(user)}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={onSignOut}
                                    data-testid="navbar-logout-btn"
                                    className="px-4 py-2 rounded-full text-[13px] font-semibold text-zrs-offwhite/90 hover:text-gold border border-white/10 hover:border-gold/40 hover:bg-gold/5 transition-all duration-300"
                                >
                                    {t("nav.signOut")}
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={onLoginClick}
                                data-testid="navbar-login-btn"
                                className="px-4 py-2 rounded-full text-[13px] font-semibold text-zrs-offwhite/90 hover:text-gold border border-white/10 hover:border-gold/40 hover:bg-gold/5 transition-all duration-300 hover:-translate-y-0.5"
                            >
                                {t("nav.login")}
                            </button>
                        )}
                        <a
                            href={discordUrl}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="navbar-discord-btn"
                            className="btn-gold btn-shimmer whitespace-nowrap px-4 py-2 rounded-full text-[13px] font-bold"
                        >
                            {t("nav.joinDiscord")}
                        </a>
                    </div>

                    <LanguageSwitch className="md:hidden ml-auto" />
                    <button
                        onClick={() => setOpen((v) => !v)}
                        data-testid="navbar-mobile-toggle"
                        className="md:hidden p-2 rounded-full text-zrs-offwhite hover:text-gold hover:bg-white/5"
                        aria-label={t("nav.menu")}
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>

                {open && (
                    <div
                        data-testid="navbar-mobile-menu"
                        className="md:hidden mt-2 glass rounded-2xl p-4 flex flex-col gap-1"
                    >
                        {LINKS.map((l) => (
                            <NavHashLink
                                key={l.to}
                                to={l.to}
                                onAfterClick={() => setOpen(false)}
                                data-testid={`mobile-nav-link-${l.id}`}
                                className="px-3 py-3 rounded-xl text-[14px] text-zrs-offwhite/85 hover:text-gold hover:bg-white/5 block"
                            >
                                {t(l.key)}
                            </NavHashLink>
                        ))}
                        <div className="h-px bg-white/10 my-2" />
                        {user ? (
                            <>
                                <Link
                                    to="/conta"
                                    onClick={() => setOpen(false)}
                                    data-testid="mobile-account-link"
                                    className={`px-3 py-3 rounded-xl text-left text-[14px] font-semibold uppercase tracking-[0.1em] ${
                                        onAccountPage ? "text-gold bg-gold/10" : "text-zrs-offwhite/90 hover:text-gold hover:bg-white/5"
                                    }`}
                                >
                                    {t("nav.accountArea")}
                                </Link>
                                <div
                                    data-testid="mobile-user"
                                    className="px-3 py-2 flex items-center gap-2"
                                >
                                    {user.user_metadata?.avatar_url ? (
                                        <img
                                            src={user.user_metadata.avatar_url}
                                            alt=""
                                            className="w-9 h-9 rounded-full border border-white/10 object-cover"
                                        />
                                    ) : null}
                                    <span className="text-[14px] text-zrs-offwhite/90 truncate">
                                        {userDisplayName(user)}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setOpen(false);
                                        onSignOut && onSignOut();
                                    }}
                                    data-testid="mobile-logout-btn"
                                    className="px-3 py-3 rounded-xl text-left text-[14px] text-zrs-offwhite/90 hover:text-gold hover:bg-white/5"
                                >
                                    {t("nav.signOut")}
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    onLoginClick?.();
                                }}
                                data-testid="mobile-login-btn"
                                className="px-3 py-3 rounded-xl text-left text-[14px] text-zrs-offwhite/90 hover:text-gold hover:bg-white/5"
                            >
                                {t("nav.login")}
                            </button>
                        )}
                        <a
                            href={discordUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-discord-btn"
                            className="mt-1 px-4 py-3 rounded-xl text-[14px] font-bold text-center whitespace-nowrap"
                            style={{ color: "#050505", background: "#FFB800" }}
                        >
                            {t("nav.joinDiscord")}
                        </a>
                    </div>
                )}
            </nav>
        </>
    );
}
